import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const normalizeKey = (key) => String(key).trim().toLowerCase().replace(/[\s-]+/g, '_');

const pad = (value) => String(value).padStart(2, '0');

// Date cells come back as Date objects (cellDates); everything else as trimmed text.
const cellText = (value) => {
  if (value instanceof Date) {
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  return String(value ?? '').trim();
};

// First sheet of an .xlsx/.xls/.csv as [{ rowNumber, <field>: text }], reading each field from the
// first of its column aliases that the sheet has (headers compared case- and space-insensitively).
export async function importSheetRows(file, columns) {
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

  return rows.map((row, index) => {
    const normalizedRow = Object.keys(row).reduce((result, key) => {
      result[normalizeKey(key)] = row[key];
      return result;
    }, {});
    const values = Object.entries(columns).reduce((result, [field, aliases]) => {
      const key = aliases.find((alias) => normalizedRow[alias] !== undefined);
      result[field] = key ? cellText(normalizedRow[key]) : '';
      return result;
    }, {});
    return { rowNumber: index + 2, ...values };
  });
}

// 277 categories E0-E4: the payer's system could not answer ("Information Holder is not responding; resubmit at
// a later time"). Nothing is wrong with the claim or the request, so these are not shown as rejected.
export const PAYER_UNAVAILABLE_CODES = ['E0', 'E1', 'E2', 'E3', 'E4'];
// Categories meaning the payer rejected the claim itself before processing it, labelled with what the payer said
// is wrong; the most specific first (A3 says only that the claim was unprocessable).
export const CLAIM_REJECTION_LABELS = {
  A6: 'Missing Info',
  A7: 'Invalid Info',
  A8: 'Inconsistent Info',
  A3: 'Unable to Process',
};
export const isClaimRejectionLabel = (label) => Object.values(CLAIM_REJECTION_LABELS).includes(label);

// Status codes that give no reason ("0" cannot provide further status electronically) or no field ("21" missing
// or invalid information): say so, and where the specifics are, instead of leaving it at the payer's wording.
const GENERIC_REJECTION_STATUS_CODES = ['0', '21'];
export function claimRejectionNote(claimStatus) {
  if (!CLAIM_REJECTION_LABELS[claimStatus?.statusCategoryCode]) return '';
  if (!GENERIC_REJECTION_STATUS_CODES.includes(String(claimStatus?.statusCode ?? ''))) return '';
  return "The payer did not say which field is wrong; the claim acknowledgement (277CA) received when the claim was submitted, or the payer's portal, names it.";
}

// 277 status category codes: F = finalized (F2 denied), P = pending, A = acknowledged, R = more info requested, E = error.
export function claimStatusLabel(record) {
  const codes = record.statusCategoryCodes ?? [];
  if (codes.some((code) => PAYER_UNAVAILABLE_CODES.includes(code))) return 'Payer Unavailable';
  if (record.payerRejected) return 'Rejected';
  if (codes.includes('F2')) return 'Denied';
  const byLetter = (prefixes) => Object.keys(prefixes).find((letter) => codes.some((code) => code.startsWith(letter)));
  const settled = byLetter({ F: 'Finalized', P: 'Pending' });
  if (settled) return { F: 'Finalized', P: 'Pending' }[settled];
  // Not every "Acknowledgement" category means received: A4 is not found, and A3 / A6-A8 mean the payer rejected
  // the claim itself, so it has to be corrected and sent again.
  if (codes.includes('A4')) return 'Not Found';
  const rejection = Object.keys(CLAIM_REJECTION_LABELS).find((code) => codes.includes(code));
  if (rejection) return CLAIM_REJECTION_LABELS[rejection];
  const prefixes = { A: 'Acknowledged', R: 'Info Requested', E: 'Error' };
  const prefix = byLetter(prefixes);
  return prefix ? prefixes[prefix] : 'Not Found';
}

// Patients are stored as "Last, First Middle"; older imports may omit the space ("LAST,FIRST").
export function splitPatientName(name) {
  const [last = '', rest = ''] = String(name ?? '').split(',');
  return { lastName: last.trim(), firstName: rest.trim().split(/\s+/)[0] ?? '' };
}

export const formatMoney = (value) => (value === null || value === undefined || value === ''
  ? '-'
  : Number(value).toLocaleString(undefined, { style: 'currency', currency: 'USD' }));

// "ACTIVE_COVERAGE" -> "Active Coverage".
export const humanize = (value) => String(value)
  .replace(/[_-]+/g, ' ')
  .toLowerCase()
  .replace(/\b[a-z]/g, (character) => character.toUpperCase());

// Stedi's possibleResolutions is markdown-ish text: "•" bullets, [label](url) links and `code` spans.
export const resolutionLines = (text) => String(text ?? '')
  .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .split(/\n+/)
  .map((line) => line.replace(/^\s*•\s*/, '').trim())
  .filter(Boolean);

const ACRONYMS = { Id: 'ID', Npi: 'NPI', Mbi: 'MBI' };

// "groupNumber" -> "Group Number", "addressLine1" -> "Address Line 1", "mbi" -> "MBI".
export const fieldLabel = (key) => String(key)
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/([a-zA-Z])(\d)/g, '$1 $2')
  .replace(/^./, (character) => character.toUpperCase())
  .replace(/\b(Id|Npi|Mbi)\b/g, (word) => ACRONYMS[word]);

// The { claim, line } of a claim status answer billed with this procedure code; the charge tells repeats of a code
// apart. Same pick as the backend (StediService.matched_service_line). null without a code or a matching line.
export function matchedServiceLine(response, procedureCode, chargeAmount) {
  const code = String(procedureCode ?? '').trim().toUpperCase();
  if (!code) return null;
  const lines = (response?.claims ?? []).flatMap((claim) => (claim?.serviceDetails ?? [])
    .filter((line) => String(line?.service?.procedureId ?? '').trim().toUpperCase() === code)
    .map((line) => ({ claim, line })));
  const amount = chargeAmount === null || chargeAmount === undefined || chargeAmount === '' ? null : Number(chargeAmount);
  const priced = amount === null ? [] : lines.filter(({ line }) => Number(line.service?.submittedAmount) === amount);
  return (priced.length ? priced : lines)[0] ?? null;
}

// Stedi sends dates as YYYY-MM-DD or YYYYMMDD, and claim service dates as a YYYYMMDD-YYYYMMDD range;
// anything unparseable is shown as is.
export const formatDate = (value) => {
  if (!value) return '-';
  const text = String(value);
  const range = text.match(/^(\d{8})-(\d{8})$/);
  if (range) return range[1] === range[2] ? formatDate(range[1]) : `${formatDate(range[1])} – ${formatDate(range[2])}`;
  const parsed = dayjs(/^\d{8}$/.test(text) ? `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6)}` : text);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : text;
};

// Wrapper keys that add nothing to a label: name.person.firstName reads "First Name", dates.plan.start "Plan Start".
const SILENT_KEYS = ['person', 'organization', 'name', 'address', 'additionalInformation', 'dates'];

// Every scalar of a nested Stedi object as [label, display value] pairs; amounts as money, dates formatted.
export function flattenFields(value, prefix = '') {
  return Object.entries(value ?? {}).flatMap(([key, item]) => {
    const label = SILENT_KEYS.includes(key) ? prefix : `${prefix} ${fieldLabel(key)}`.trim();
    if (item === null || item === undefined || item === '') return [];
    if (Array.isArray(item)) {
      return item.every((entry) => typeof entry !== 'object') ? [[label, item.join(', ')]] : [];
    }
    if (typeof item === 'object') return flattenFields(item, label);
    const name = label || fieldLabel(key);
    if (/amount/i.test(key) && !Number.isNaN(Number(item))) return [[name, formatMoney(item)]];
    return [[name, /date|start|end/i.test(name) ? formatDate(item) : String(item)]];
  });
}

// Bulk upload columns and the other headers accepted for them, for the upload preview. Keep in step with the
// backend importer (aegis360-api services/stedi_bulk_service.py, COLUMNS), which also builds the template.
const TEMPLATE_COLUMNS = {
  firstName: { header: 'First Name', aliases: ['first_name', 'firstname', 'first'], required: true },
  lastName: { header: 'Last Name', aliases: ['last_name', 'lastname', 'last'], required: true },
  dateOfBirth: { header: 'Date of Birth', aliases: ['date_of_birth', 'dob', 'dateofbirth', 'birth_date', 'birthdate'], required: true },
  memberId: { header: 'Member ID', aliases: ['member_id', 'memberid', 'member', 'subscriber_id', 'subscriberid'], required: true },
  insuranceName: { header: 'Insurance Name', aliases: ['insurance_name', 'insurancename', 'insurance', 'payer_name', 'payername', 'payer', 'payer_id', 'payerid'], required: false },
  serviceDateFrom: { header: 'Service Date From', aliases: ['service_date_from', 'date_of_service_from', 'dos_from', 'beginning_date_of_service'], required: false },
  serviceDateTo: { header: 'Service Date To', aliases: ['service_date_to', 'date_of_service_to', 'dos_to', 'end_date_of_service'], required: false },
  providerName: { header: 'Billing Provider Name', aliases: ['billing_provider_name', 'billing_provider', 'provider_name', 'provider', 'organization_name'], required: true },
  npi: { header: 'Billing Provider NPI', aliases: ['billing_provider_npi', 'billing_npi', 'provider_npi', 'npi'], required: true },
  procedureCode: { header: 'Procedure Code', aliases: ['procedure_code', 'procedurecode', 'procedure', 'cpt', 'cpt_code', 'cpt_hcpcs', 'hcpcs', 'hcpcs_code'], required: false },
  lineChargeAmount: { header: 'Charge Amount', aliases: ['charge_amount', 'line_charge_amount', 'charge', 'charges', 'billed_amount', 'billed', 'submitted_amount', 'amount'], required: false },
};

const TEMPLATE_FIELDS = {
  eligibility: ['firstName', 'lastName', 'dateOfBirth', 'memberId', 'insuranceName'],
  'claim-status': ['firstName', 'lastName', 'dateOfBirth', 'memberId', 'insuranceName', 'serviceDateFrom', 'serviceDateTo', 'procedureCode', 'lineChargeAmount', 'providerName', 'npi'],
};

export const templateColumns = (kind) => TEMPLATE_FIELDS[kind].map((field) => ({ field, ...TEMPLATE_COLUMNS[field] }));

// { field: aliases } for importSheetRows.
export const templateAliases = (kind) => Object.fromEntries(templateColumns(kind).map(({ field, aliases }) => [field, aliases]));

// Insurance names and payer IDs compare ignoring case, spaces and punctuation, as the backend does.
export const payerKey = (value) => String(value ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// { "<Insurance Name in the file>": payer ID } for the names the user mapped to a payer on the import preview.
export const insuranceMappings = (rows) => Object.fromEntries(
  rows.filter((row) => row.mappedPayerId).map((row) => [row.insuranceName, row.mappedPayerId]),
);

// Service type codes for mental health (MH and its psychiatric / psychotherapy / substance abuse variants,
// psychiatric partial hospitalization / day care, and mental health / substance abuse provider and facility codes).
export const MENTAL_HEALTH_CODES = ['MH', 'A4', 'A5', 'A6', 'A7', 'A8', 'AI', 'AJ', 'AK', 'BB', 'BC', 'CE', 'CF', 'CG', 'CH', 'CI', 'CJ', 'RT'];

// Stedi's original 271 format (benefitsInformation[], what batch checks return): benefit list per code, and the
// network indicator spelled the way plans[] spells it.
const CLASSIC_BENEFIT_TYPES = {
  1: 'statuses', 2: 'statuses', 3: 'statuses', 4: 'statuses', 5: 'statuses', 6: 'statuses', 7: 'statuses', 8: 'statuses',
  I: 'statuses', A: 'coInsurance', B: 'coPayment', C: 'deductible', G: 'outOfPocket', F: 'limitations',
};
const CLASSIC_NETWORKS = { Y: 'IN_NETWORK', N: 'OUT_OF_NETWORK', W: 'IN_AND_OUT_OF_NETWORK' };

// One benefitsInformation entry as plans[]-style entries, one per service type code it covers.
function classicEntries(entry) {
  const related = entry.benefitsRelatedEntity ?? {};
  const relatedName = [related.entityFirstname, related.entityName].filter(Boolean).join(' ');
  const base = {
    status: CLASSIC_BENEFIT_TYPES[entry.code] === 'statuses' ? entry.name : undefined,
    amount: entry.benefitAmount,
    percent: entry.benefitPercent,
    network: entry.inPlanNetworkIndicatorCode ? { indicator: CLASSIC_NETWORKS[entry.inPlanNetworkIndicatorCode] } : undefined,
    coverageLevel: entry.coverageLevel,
    timePeriod: entry.timeQualifier,
    insuranceType: entry.insuranceType,
    planCoverageDescription: entry.planCoverage,
    // Qualifier ZZ carries the place of service (11 office, 21 inpatient hospital...), as plans[] has it.
    placesOfService: (entry.eligibilityAdditionalInformationList
      ?? (entry.eligibilityAdditionalInformation ? [entry.eligibilityAdditionalInformation] : []))
      .filter((info) => info?.codeListQualifierCode === 'ZZ' && info.industryCode)
      .map((info) => ({ code: info.industryCode, name: info.industry })),
    messages: [
      relatedName && `${related.entityIdentifier || 'Contact'}: ${relatedName}${related.entityIdentificationValue ? ` (${related.entityIdentificationValue})` : ''}`,
      entry.authOrCertIndicator === 'Y' && 'Prior authorization required',
      ...(entry.additionalInformation ?? []).map((info) => info.description),
    ].filter(Boolean),
  };
  const codes = entry.serviceTypeCodes ?? [];
  if (!codes.length) return [base];
  return codes.map((code, index) => ({ ...base, service: { system: 'STC', value: code, definition: entry.serviceTypes?.[index] } }));
}

// The response's plans in the plans[] format the view renders: plans[] as is (real-time checks), or the original
// benefitsInformation[] format (batch checks) as one plan.
export function responsePlans(response) {
  if (Array.isArray(response?.plans) && response.plans.length) return response.plans;
  const benefits = response?.benefitsInformation;
  if (!Array.isArray(benefits) || !benefits.length) return [];
  const grouped = {};
  benefits.forEach((entry) => {
    const type = CLASSIC_BENEFIT_TYPES[entry.code] ?? entry.name ?? `Code ${entry.code}`;
    grouped[type] = [...(grouped[type] ?? []), ...classicEntries(entry)];
  });
  const info = response.planInformation ?? {};
  const dates = response.planDateInformation ?? {};
  return [{
    name: benefits.find((entry) => entry.code === '1' && entry.planCoverage)?.planCoverage
      || info.planDescription || info.groupDescription,
    groupNumber: info.groupNumber,
    planNumber: info.planNumber,
    planDates: dates.plan && String(dates.plan).split('-').map(formatDate).join(' – '),
    planBegin: dates.planBegin,
    planEnd: dates.planEnd,
    benefits: grouped,
  }];
}
